import { Component, DestroyRef, effect, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core'
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps'
import { ActivatedRoute } from '@angular/router'
import { EngineerTrackingService } from './services/engineer-tracking.service'
import { EngineerTrackingResponse } from './interfaces/engineer-tracking-response.interface'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { TrackingUnavailableComponent } from './components/tracking-unavailable/tracking-unavailable.component'
import { TrackingInformationComponent } from './components/tracking-information/tracking-information.component'
import { SignalRService } from 'src/app/core/services/signalr/signalr-service'
import { EntityType } from 'src/app/shared/signalr/change-notification.interface'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { EngineerTrackingEvent } from 'src/app/shared/signalr/engineer-tracking-event.interface'

@Component({
    selector: 'sf-engineer-tracking',
    imports: [GoogleMapsModule, FontAwesomeModule, TrackingUnavailableComponent, TrackingInformationComponent],
    templateUrl: './engineer-tracking.component.html',
    styleUrls: ['./engineer-tracking.component.scss'],
})
export class EngineerTrackingComponent implements OnInit, OnDestroy {
    #activatedRoute = inject(ActivatedRoute)
    #engineerTrackingService = inject(EngineerTrackingService)
    #mapDirectionsService = new google.maps.DirectionsService()
    #signalRService = inject(SignalRService)
    #destroyRef = inject(DestroyRef)

    @ViewChild(GoogleMap) map!: GoogleMap

    mapMarkers: google.maps.marker.AdvancedMarkerElement[] = []

    engineerTrackingId = signal<string | null>(null)
    trackingInformationUnavailable = signal(false)
    engineerTracking = signal<EngineerTrackingResponse | null>(null)
    directionsResult = signal<google.maps.DirectionsResult | null>(null)

    signalR = effect(() => {
        const id = this.engineerTrackingId()
        if (id == null || this.#signalRService.publicConnectionEstablished() == false) return

        this.#signalRService.joinEngineerTrackingGroup(id)
        this.#signalRService
            .getChangesFor<EngineerTrackingEvent>(EntityType.EngineerTracking)
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe((change) => {
                const { lat, lng } = change.data
                this.engineerTracking.update((current) =>
                    current ? { ...current, engineerMarker: { lat, lng } } : null
                )
            })
    })

    engineerTrackingUpdates = effect(() => {
        const engineerTracking = this.engineerTracking()
        if (engineerTracking) {
            this.fitMapBounds(true, engineerTracking)
            this.fetchDirections(engineerTracking)
        }
    })

    fitMapBounds(useBottomPadding: boolean, engineerTracking?: EngineerTrackingResponse): void {
        const trackingData = engineerTracking ?? this.engineerTracking()
        if (!trackingData || !this.map?.googleMap) return

        const { engineerMarker, customerMarker } = trackingData

        const bounds = new google.maps.LatLngBounds()
        bounds.extend(new google.maps.LatLng(engineerMarker.lat, engineerMarker.lng))
        bounds.extend(new google.maps.LatLng(customerMarker.lat, customerMarker.lng))

        this.map.googleMap.fitBounds(bounds, { top: 150, bottom: useBottomPadding ? 450 : 100, left: 50, right: 50 })
    }

    addMapMarker(position: { lat: number; lng: number }, src: string, title: string) {
        if (!this.map?.googleMap) return

        const glyphImg = document.createElement('img')
        glyphImg.src = src
        glyphImg.style.width = '32px'
        glyphImg.style.height = '32px'

        const pinElement = new google.maps.marker.PinElement({
            glyph: glyphImg,
            background: '#1976D2',
            borderColor: '#0D47A1',
            scale: 1.5,
        })

        const markerElement = new google.maps.marker.AdvancedMarkerElement({
            map: this.map.googleMap,
            position: new google.maps.LatLng(position.lat, position.lng),
            content: pinElement.element,
            title,
        })

        this.mapMarkers.push(markerElement)
    }

    clearMapMarkers() {
        for (const marker of this.mapMarkers) {
            marker.map = null
        }

        this.mapMarkers = []
    }

    initializeMapBounds(): void {
        const mapReadySub = this.map.boundsChanged.subscribe(() => {
            mapReadySub.unsubscribe()

            this.fitMapBounds(true)

            const engineerTracking = this.engineerTracking()
            if (engineerTracking) {
                const { engineerMarker, customerMarker } = engineerTracking
                this.clearMapMarkers()
                this.addMapMarker(customerMarker, '/assets/icons/house-solid-full.svg', 'Customer')
                this.addMapMarker(engineerMarker, '/assets/icons/car-solid-full.svg', 'Engineer')
            }
        })
    }

    fetchDirections(data: EngineerTrackingResponse): void {
        const engineerMarker = data.engineerMarker
        const customerMarker = data.customerMarker

        this.#mapDirectionsService.route(
            {
                origin: { lat: engineerMarker.lat, lng: engineerMarker.lng },
                destination: { lat: customerMarker.lat, lng: customerMarker.lng },
                travelMode: google.maps.TravelMode.DRIVING,
            },
            (result, status) => {
                const isSuccess = status === google.maps.DirectionsStatus.OK
                this.trackingInformationUnavailable.set(!isSuccess)
                if (isSuccess) {
                    this.directionsResult.set(result)
                    this.initializeMapBounds()
                }
            }
        )
    }

    loadEngineerTracking(id: string): void {
        this.engineerTrackingId.set(id)
        this.#engineerTrackingService.getEngineerTracking(id).subscribe({
            next: (trackingData) => {
                this.engineerTracking.set(trackingData)
            },
            error: () => {
                this.trackingInformationUnavailable.set(true)
            },
        })
    }

    ngOnInit(): void {
        this.#signalRService.initiatePublicSignalrConnection()

        this.#activatedRoute.paramMap.subscribe((params) => {
            const id = params.get('id')

            if (id) {
                this.loadEngineerTracking(id)
            }
        })
    }

    ngOnDestroy(): void {
        const id = this.engineerTrackingId()

        if (id) {
            this.#signalRService.leaveEngineerTrackingGroup(id)
        }
    }
}
